import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { useEffect, useMemo, useState } from "react";

import { getUserInitials, isInactiveMember } from "@utils/users.util";
import { normalizeLogoUrl } from "@utils/logo.util";
import { cn } from "@utils/tailwind.util";
import useUserStore from "@store/useUserStore";
import { IPeople } from "@interfaces/people.interface";
import { User } from "@app-types/common.type";
import { Tooltip, TooltipProps } from "../Tooltip";
import { Button } from "../Button";

export interface ILetterAvatarListProps {
  people: (User | IPeople)[];
  numberVisible?: number;
  tooltipContentClassName?: string;
  size?: LetterAvatarListSize;
  onViewAll?: () => void;
  useTagStyle?: boolean;
  maxItems?: number;
  placement?: "top" | "bottom" | "left" | "right" | "bottom-start";
  tooltipProps?: Partial<TooltipProps<"div">>;
}

export function RenderAvatar({
  className,
  avatarUrl: rawAvatarUrl,
  showBorder = true,
  text,
  style,
  sizeClassName = "h-[26px] w-[26px]",
  onClick,
}: {
  className?: string;
  avatarUrl?: string;
  showBorder?: boolean;
  text?: string;
  sizeClassName?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
}) {
  // TODO: remove normalizeLogoUrl once the Scout Agent index is
  // re-built with the new URL format from BE.
  const avatarUrl = normalizeLogoUrl(rawAvatarUrl);
  const borderColor = showBorder ? "border-white" : "border-transparent";
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);

  const hasValidAvatar = Boolean(avatarUrl && !imageFailed);

  return (
    <div
      className={cn(
        "relative inline-flex select-none items-center justify-center overflow-hidden rounded-full border-2 bg-gray-200 text-[10px] font-medium uppercase text-gray-600",
        borderColor,
        className,
        sizeClassName,
        onClick ? "cursor-pointer" : ""
      )}
      style={style}
      onClick={onClick}
    >
      {hasValidAvatar ? (
        <img
          src={avatarUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="relative text-[#9CA1AF]">{text}</span>
      )}
    </div>
  );
}

function isUser(person: User | IPeople): person is User {
  return (person as User).avatar_url !== undefined;
}

function generateToolTip(
  people: (User | IPeople)[],
  sizeClassName: string,
  onViewAll?: () => void,
  options?: { useTagStyle?: boolean; maxItems?: number; tagSize?: LetterAvatarListSize }
) {
  const { activeUser: userInfo } = useUserStore();
  const domains = userInfo?.organization?.domains;
  const MAX_ITEMS = options?.maxItems || 15;
  const showViewAll = people.length > MAX_ITEMS && onViewAll !== undefined;
  const displayPeople = showViewAll ? people.slice(0, MAX_ITEMS) : people;
  const useTagStyle = options?.useTagStyle || false;
  const tagSize = options?.tagSize || LetterAvatarListSize.MEDIUM;

  const rows = displayPeople.map((person) => {
    if (!person || typeof person !== "object") return null;

    const initials = getUserInitials(person as unknown as User);
    const hasName = !isEmpty(person?.first_name) || !isEmpty(person?.last_name);
    const isUnrecognizedOrInactive = isInactiveMember(person as User, domains);

    const className = cn({
      "bg-background-secondary": isUnrecognizedOrInactive,
    });

    const titleText = isUser(person) ? get(person, "title.title", "") : "";

    return (
      <Tooltip
        key={person.id}
        placement="top"
        variant="dark"
        tooltipContent={"Unrecognized or inactive member"}
        arrow={true}
        className="w-fit max-w-full"
        disabled={!isUnrecognizedOrInactive}
      >
        <div
          className={cn("flex items-center gap-1 py-0 text-sm", {
            "max-h-[400px] max-w-[400px] rounded-md bg-background-tagMemberDefault p-1 py-0 pr-2 shadow-scoutButton":
              useTagStyle,
            "h-5 text-xs [&_span.relative]:!text-[8px]":
              useTagStyle && tagSize === LetterAvatarListSize.SMALL,
            "h-7 text-sm [&_span.relative]:!text-[8px]":
              useTagStyle && tagSize === LetterAvatarListSize.MEDIUM,
          })}
          key={"tooltip-" + person?.id}
        >
          <div className="flex-shrink-0">
            {person && isUser(person) ? (
              <RenderAvatar
                text={initials}
                className={className}
                avatarUrl={person?.avatar_url}
                sizeClassName={sizeClassName}
                showBorder={!useTagStyle}
              />
            ) : (
              <RenderAvatar
                avatarUrl={
                  person?.primary_company?.company.is_suggested
                    ? undefined
                    : person?.primary_company?.company.logo_url
                }
                className={className}
                text={initials}
                sizeClassName={sizeClassName}
                showBorder={!useTagStyle}
              />
            )}
          </div>
          {hasName && (
            <span className="truncate font-medium">
              {person?.first_name ?? ""} {person?.last_name ?? ""}
            </span>
          )}
          {!hasName && <span className="truncate font-medium">{person?.email}</span>}
          {titleText && (
            <span className="flex-shrink-0 font-normal text-content-secondary">
              {String(titleText)}
            </span>
          )}
        </div>
      </Tooltip>
    );
  });

  return (
    <div
      className={cn(
        "flex max-h-[400px] max-w-[400px] flex-col items-start gap-2 overflow-y-auto p-2",
        {
          "gap-1": useTagStyle && tagSize === LetterAvatarListSize.SMALL,
          "gap-2": useTagStyle && tagSize === LetterAvatarListSize.MEDIUM,
        }
      )}
    >
      {rows}
      {showViewAll && (
        <Button
          buttonType="TRANSPARENT"
          buttonSize="SMALL"
          onClick={(e) => {
            e.stopPropagation();
            onViewAll?.();
          }}
          className="max-h-[20px] cursor-pointer !bg-transparent p-0 text-xs text-gray-450 hover:!bg-transparent hover:text-gray-600"
        >
          View All
        </Button>
      )}
    </div>
  );
}

export enum LetterAvatarListSize {
  SMALL = "small",
  MEDIUM = "medium",
}

export function LetterAvatar({
  person,
  text,
  user,
  showBorder,
  sizeClassName = "h-[26px] w-[26px]",
  onClick,
}: {
  person?: User | IPeople;
  text?: string;
  user?: User;
  showBorder?: boolean;
  sizeClassName?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  if (user && !person) {
    const initials = getUserInitials(user);
    return (
      <div className={cn("z-0 flex rounded-full [&:not(:first-child)]:-ml-2", sizeClassName)}>
        <RenderAvatar
          text={initials}
          avatarUrl={"avatar_url" in user ? user?.avatar_url : null}
          showBorder={showBorder}
          sizeClassName={sizeClassName}
          onClick={onClick}
        />
      </div>
    );
  } else {
    const initials = person !== undefined ? getUserInitials(person) : text;

    return (
      <div className={cn("z-0 flex rounded-full [&:not(:first-child)]:-ml-2", sizeClassName)}>
        {person && isUser(person) ? (
          <RenderAvatar
            text={initials}
            avatarUrl={person?.avatar_url}
            showBorder={showBorder}
            sizeClassName={sizeClassName}
            onClick={onClick}
          />
        ) : (
          <RenderAvatar
            avatarUrl={
              person?.primary_company?.company.is_suggested
                ? undefined
                : person?.primary_company?.company.logo_url
            }
            text={initials}
            showBorder={showBorder}
            sizeClassName={sizeClassName}
            onClick={onClick}
          />
        )}
      </div>
    );
  }
}

export function LetterAvatarList({
  people,
  numberVisible = 4,
  tooltipContentClassName = "",
  size = LetterAvatarListSize.MEDIUM,
  onViewAll,
  useTagStyle = false,
  maxItems,
  placement = "bottom",
  tooltipProps = {},
}: ILetterAvatarListProps) {
  const renderClassNamesBySize = useMemo(() => {
    if (size === LetterAvatarListSize.SMALL) return "h-[22px] w-[22px]";
    return "h-[26px] w-[26px]";
  }, [size]);

  const tooltipRows = generateToolTip(people, renderClassNamesBySize, onViewAll, {
    useTagStyle,
    maxItems,
    tagSize: size,
  });

  return (
    <Tooltip
      {...tooltipProps}
      tooltipContent={tooltipRows}
      tooltipContentClassName={`${tooltipContentClassName} p-0 z-30`}
      placement={placement}
    >
      <div className="flex select-none">
        {people
          .slice(0, people.length > numberVisible ? numberVisible - 1 : numberVisible)
          .map((person) => (
            <LetterAvatar
              key={person?.id}
              person={person}
              sizeClassName={renderClassNamesBySize}
              showBorder
            />
          ))}
        {people.length > numberVisible && (
          <LetterAvatar
            text={`+${people.length - numberVisible + 1}`}
            sizeClassName={renderClassNamesBySize}
            showBorder
          />
        )}
      </div>
    </Tooltip>
  );
}
